import { supabase } from 'utils/supabase'
import { MEDIATOR_SETTINGS_TABLE } from 'constants/db.contants'
import { ONBOARDING_STEP_CONFIG, TOTAL_ONBOARDING_STEPS } from 'constants/onboarding.constant'

class OnboardingTourService {
    /**
     * Get onboarding status from mediator_settings table
     * @param {number} mediatorId - Mediator ID
     * @returns {Promise<Object>} Onboarding status object
     */
    async getOnboardingStatus(mediatorId) {
        try {
            const { data, error } = await supabase
                .from(MEDIATOR_SETTINGS_TABLE)
                .select('portal_onboarding_completed, portal_onboarding_current_step, portal_onboarding_started_at, portal_onboarding_completed_at')
                .eq('mediator_id', mediatorId)
                .maybeSingle()

            if (error) {
                console.error('Error fetching onboarding status:', error)
                return {
                    completed: false,
                    currentStep: 1,
                    startedAt: null,
                    completedAt: null,
                }
            }

            if (!data) {
                return {
                    completed: false,
                    currentStep: 1,
                    startedAt: null,
                    completedAt: null,
                }
            }

            return {
                completed: data.portal_onboarding_completed || false,
                currentStep: data.portal_onboarding_current_step || 1,
                startedAt: data.portal_onboarding_started_at || null,
                completedAt: data.portal_onboarding_completed_at || null,
            }
        } catch (error) {
            console.error('Error in getOnboardingStatus:', error)
            return {
                completed: false,
                currentStep: 1,
                startedAt: null,
                completedAt: null,
            }
        }
    }

    /**
     * Update current onboarding step
     * @param {number} mediatorId - Mediator ID
     * @param {number} step - Step number (1-6)
     * @returns {Promise<Object>} Success status
     */
    async updateStep(mediatorId, step) {
        try {
            const { data: existingData, error: checkError } = await supabase
                .from(MEDIATOR_SETTINGS_TABLE)
                .select('mediator_id, portal_onboarding_started_at')
                .eq('mediator_id', mediatorId)
                .maybeSingle()

            const updateData = {
                portal_onboarding_current_step: step,
                updated_at: new Date().toISOString(),
            }

            if (step === 1 && (!existingData || !existingData.portal_onboarding_started_at)) {
                updateData.portal_onboarding_started_at = new Date().toISOString()
            }

            let result

            if (existingData) {
                const { error } = await supabase
                    .from(MEDIATOR_SETTINGS_TABLE)
                    .update(updateData)
                    .eq('mediator_id', mediatorId)

                if (error) {
                    console.error('Error updating onboarding step:', error)
                    return { success: false, error: error.message }
                }
                result = { success: true }
            } else {
                const { error } = await supabase
                    .from(MEDIATOR_SETTINGS_TABLE)
                    .insert({
                        mediator_id: mediatorId,
                        ...updateData,
                    })

                if (error) {
                    console.error('Error creating onboarding record:', error)
                    return { success: false, error: error.message }
                }
                result = { success: true }
            }

            return result
        } catch (error) {
            console.error('Error in updateStep:', error)
            return { success: false, error: error.message }
        }
    }

    /**
     * Mark onboarding as completed
     * @param {number} mediatorId - Mediator ID
     * @returns {Promise<Object>} Success status
     */
    async completeOnboarding(mediatorId) {
        try {
            const { error } = await supabase
                .from(MEDIATOR_SETTINGS_TABLE)
                .update({
                    portal_onboarding_completed: true,
                    portal_onboarding_current_step: 0,
                    portal_onboarding_completed_at: new Date().toISOString(),
                    updated_at: new Date().toISOString(),
                })
                .eq('mediator_id', mediatorId)

            if (error) {
                console.error('Error completing onboarding:', error)
                return { success: false, error: error.message }
            }

            return { success: true }
        } catch (error) {
            console.error('Error in completeOnboarding:', error)
            return { success: false, error: error.message }
        }
    }

    /**
     * Skip onboarding tour
     * @param {number} mediatorId - Mediator ID
     * @returns {Promise<Object>} Success status
     */
    async skipOnboarding(mediatorId) {
        try {
            const { error } = await supabase
                .from(MEDIATOR_SETTINGS_TABLE)
                .update({
                    portal_onboarding_completed: true,
                    portal_onboarding_current_step: 0,
                    portal_onboarding_completed_at: new Date().toISOString(),
                    updated_at: new Date().toISOString(),
                })
                .eq('mediator_id', mediatorId)

            if (error) {
                console.error('Error skipping onboarding:', error)
                return { success: false, error: error.message }
            }

            return { success: true }
        } catch (error) {
            console.error('Error in skipOnboarding:', error)
            return { success: false, error: error.message }
        }
    }

    /**
     * Get step configuration
     * @param {number} step - Step number
     * @returns {Object|null} Step configuration
     */
    getStepConfig(step) {
        return ONBOARDING_STEP_CONFIG[step] || null
    }

    /**
     * Check if onboarding should be shown
     * @param {Object} onboardingStatus - Onboarding status object
     * @returns {boolean} True if should show onboarding
     */
    shouldShowOnboarding(onboardingStatus) {
        return !onboardingStatus.completed && onboardingStatus.currentStep > 0
    }
}

export default new OnboardingTourService()
